import { I18n } from 'react-redux-i18n';
import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import {
  DistributionGlobalDataFilter,
  useV2GlobalViewWines
} from 'apiTyped/hooks/global_data_view.query';
import { SearchInput } from '../components/SearchInput';
import { SectionInfo } from '../components/SectionInfo';
import {
  BrandExpandButton,
  BrandWineRows,
  BrandWineScope
} from '../components/BrandWineRows';
import {
  ColumnDisplayModal,
  ColumnDef
} from '../components/ColumnDisplayModal';
import { Button } from '../components/Button';
import { TabBar } from '../components/TabBar';
import {
  ExportButton,
  GroupByBtn,
  RowLimitBar,
  RowLimitFooter
} from './GlobalView/shared';
import { TableSkeleton } from './GlobalView/loading';
import { downloadCsv } from '../utils/downloadCsv';
import {
  SortableHeaderButton,
  useTableSort
} from '../components/SortableTable';
import {
  useV2DistributionMarketSummary,
  V2DistributionPosFilter
} from 'apiTyped/hooks/v2.distribution.query';

type MarketScopeFilter = Omit<
  DistributionGlobalDataFilter,
  'aggregation_type' | 'selected_client_ref_ids' | 'selected_client_custom_ids'
>;

const RANKING_LIMIT_OPTIONS = [20, 50, 100] as const;
type RankingLimit = (typeof RANKING_LIMIT_OPTIONS)[number];

type MarketPositioningProps = {
  filter: MarketScopeFilter;
  summaryFilter: V2DistributionPosFilter;
  posRows: unknown[];
  isLoadingPos: boolean;
};

type MarketRow = {
  marketId: number;
  rank: number;
  market: string;
  categoryND: number;
  categoryPen: number;
  stores: number;
  posGained: number;
  posLost: number;
  clientND: number;
  clientPen: number;
  rivalND: number;
  rivalPen: number;
  competitivePoolND: number;
  competitivePoolPen: number;
  ndLastYear: number | null;
  notSurveyed: number | null;
  newPosSurveyed: number | null;
};

type MarketColumn = Omit<ColumnDef, 'label'> & { labelKey: string };

const MARKET_COLUMNS: MarketColumn[] = [
  {
    id: 'market',
    labelKey: 'v2GlobalView.marketOverview.marketRanking.columns.market',
    defaultOn: true,
    always: true
  },
  {
    id: 'stores',
    labelKey: 'v2GlobalView.marketOverview.marketRanking.columns.stores',
    defaultOn: false
  },
  {
    id: 'categoryND',
    labelKey: 'v2GlobalView.marketOverview.marketRanking.columns.categoryND',
    defaultOn: true
  },
  {
    id: 'categoryPen',
    labelKey: 'v2GlobalView.marketOverview.marketRanking.columns.categoryPen',
    defaultOn: true
  },
  {
    id: 'clientND',
    labelKey: 'v2GlobalView.marketOverview.marketRanking.columns.clientND',
    defaultOn: true
  },
  {
    id: 'clientPen',
    labelKey: 'v2GlobalView.marketOverview.marketRanking.columns.clientPen',
    defaultOn: true
  },
  {
    id: 'ndLastYear',
    labelKey: 'v2GlobalView.marketOverview.marketRanking.columns.ndLastYear',
    defaultOn: false
  },
  {
    id: 'posGained',
    labelKey: 'v2GlobalView.marketOverview.marketRanking.columns.posGained',
    defaultOn: true
  },
  {
    id: 'posLost',
    labelKey: 'v2GlobalView.marketOverview.marketRanking.columns.posLost',
    defaultOn: true
  },
  {
    id: 'notSurveyed',
    labelKey: 'v2GlobalView.marketOverview.marketRanking.columns.notSurveyed',
    defaultOn: false
  },
  {
    id: 'newPosSurveyed',
    labelKey:
      'v2GlobalView.marketOverview.marketRanking.columns.newPosSurveyed',
    defaultOn: false
  },
  {
    id: 'rivalND',
    labelKey: 'v2GlobalView.marketOverview.marketRanking.columns.rivalND',
    defaultOn: false
  },
  {
    id: 'rivalPen',
    labelKey: 'v2GlobalView.marketOverview.marketRanking.columns.rivalPen',
    defaultOn: false
  },
  {
    id: 'competitivePoolND',
    labelKey:
      'v2GlobalView.marketOverview.marketRanking.columns.competitivePoolND',
    defaultOn: false
  },
  {
    id: 'competitivePoolPen',
    labelKey:
      'v2GlobalView.marketOverview.marketRanking.columns.competitivePoolPen',
    defaultOn: true
  }
];

const getMarketColumns = (): ColumnDef[] =>
  MARKET_COLUMNS.map(({ labelKey, ...column }) => ({
    ...column,
    label: I18n.t(labelKey)
  }));

const defaultMarketColumnVisibility = (): Record<string, boolean> =>
  MARKET_COLUMNS.reduce<Record<string, boolean>>((visibility, column) => {
    visibility[column.id] = column.defaultOn;
    return visibility;
  }, {});

type RankingRow = {
  entityId?: number;
  name: string;
  color?: string | null;
  accounts: number;
  variation: number;
  refs: number;
  price: number | null;
  prevAccounts: number;
  rank?: number;
  prevRank?: number;
  rankMove?: number;
};

const toNumber = (value: unknown): number =>
  typeof value === 'number' && Number.isFinite(value)
    ? value
    : typeof value === 'string'
      ? value
          .split(',')
          .map((item) => item.trim())
          .filter(Boolean).length
      : 0;

const nameOf = (row: Record<string, unknown>): string =>
  String(row.name ?? row.entity_name ?? '—');

const RankingTable: React.FC<{
  rows: RankingRow[];
  showColor?: boolean;
  filter: BrandWineScope;
}> = ({ rows, showColor = false, filter }) => {
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  const { sortedRows, sort, onSort } = useTableSort(
    rows,
    (row, key) => row[key as keyof RankingRow]
  );
  const toggle = (entityId: number) =>
    setExpanded((previous) => {
      const next = new Set(previous);
      if (next.has(entityId)) next.delete(entityId);
      else next.add(entityId);
      return next;
    });
  return (
    <Table>
      <thead>
        <tr>
          <Th aria-sort={sort?.key === 'rank' ? sort.direction : 'none'}>
            <SortableHeaderButton
              label="#"
              active={sort?.key === 'rank'}
              direction={sort?.direction}
              onClick={() => onSort('rank')}
            />
          </Th>
          <Th aria-sort={sort?.key === 'name' ? sort.direction : 'none'}>
            <SortableHeaderButton
              label={I18n.t(
                showColor
                  ? 'v2GlobalView.marketOverview.wineRanking.columns.wine'
                  : 'v2GlobalView.marketOverview.brandRanking.columns.brand'
              )}
              active={sort?.key === 'name'}
              direction={sort?.direction}
              onClick={() => onSort('name')}
            />
          </Th>
          {showColor && (
            <Th aria-sort={sort?.key === 'color' ? sort.direction : 'none'}>
              <SortableHeaderButton
                label={I18n.t(
                  'v2GlobalView.marketOverview.wineRanking.columns.color'
                )}
                active={sort?.key === 'color'}
                direction={sort?.direction}
                onClick={() => onSort('color')}
              />
            </Th>
          )}
          <Th
            $align="right"
            aria-sort={sort?.key === 'accounts' ? sort.direction : 'none'}
          >
            <SortableHeaderButton
              align="right"
              label={I18n.t(
                'v2GlobalView.marketOverview.wineRanking.columns.accounts'
              )}
              active={sort?.key === 'accounts'}
              direction={sort?.direction}
              onClick={() => onSort('accounts')}
            />
          </Th>
          <Th
            $align="right"
            aria-sort={sort?.key === 'variation' ? sort.direction : 'none'}
          >
            <SortableHeaderButton
              align="right"
              label={I18n.t(
                'v2GlobalView.marketOverview.wineRanking.columns.variation'
              )}
              active={sort?.key === 'variation'}
              direction={sort?.direction}
              onClick={() => onSort('variation')}
            />
          </Th>
          <Th
            $align="right"
            aria-sort={sort?.key === 'refs' ? sort.direction : 'none'}
          >
            <SortableHeaderButton
              align="right"
              label={I18n.t(
                'v2GlobalView.marketOverview.wineRanking.columns.refs'
              )}
              active={sort?.key === 'refs'}
              direction={sort?.direction}
              onClick={() => onSort('refs')}
            />
          </Th>
          <Th
            $align="right"
            aria-sort={sort?.key === 'price' ? sort.direction : 'none'}
          >
            <SortableHeaderButton
              align="right"
              label={I18n.t(
                'v2GlobalView.marketOverview.wineRanking.columns.price'
              )}
              active={sort?.key === 'price'}
              direction={sort?.direction}
              onClick={() => onSort('price')}
            />
          </Th>
        </tr>
      </thead>
      <tbody>
        {rows.length === 0 ? (
          <tr>
            <Empty colSpan={showColor ? 7 : 6}>
              {I18n.t(
                showColor
                  ? 'v2GlobalView.marketOverview.wineRanking.noData'
                  : 'v2GlobalView.marketOverview.brandRanking.noData'
              )}
            </Empty>
          </tr>
        ) : (
          sortedRows.map((row, index) => (
            <React.Fragment key={row.entityId ?? `${row.name}-${index}`}>
              <tr>
                <Td>
                  <Rank>{row.rank ?? index + 1}</Rank>
                  {row.rankMove ? (
                    <RankMove $positive={row.rankMove > 0}>
                      ({row.rankMove > 0 ? '+' : ''}
                      {row.rankMove})
                    </RankMove>
                  ) : (
                    <RankMove>(-)</RankMove>
                  )}
                </Td>
                <Td>
                  {!showColor && row.entityId != null && (
                    <BrandExpandButton
                      name={row.name}
                      expanded={expanded.has(row.entityId)}
                      onClick={() => toggle(row.entityId!)}
                    />
                  )}
                  <strong>{row.name}</strong>
                </Td>
                {showColor && <Td>{row.color || '—'}</Td>}
                <TdNum>{row.accounts}</TdNum>
                <TdNum
                  $positive={row.variation > 0}
                  $negative={row.variation < 0}
                >
                  {row.variation > 0 ? '+' : ''}
                  {row.variation}
                </TdNum>
                <TdNum>{row.refs}</TdNum>
                <TdNum>{row.price == null ? '—' : row.price.toFixed(2)}</TdNum>
              </tr>
              {!showColor &&
                row.entityId != null &&
                expanded.has(row.entityId) && (
                  <BrandWineRows
                    brandId={row.entityId}
                    filter={filter}
                    colSpan={6}
                    renderRow={(wine) => (
                      <WineChildRow key={wine.entity_id ?? wine.entity_name}>
                        <Td />
                        <Td>{wine.entity_name}</Td>
                        <TdNum>{wine.dn}</TdNum>
                        <TdNum>
                          {toNumber(wine.won_pos) - toNumber(wine.lost_pos)}
                        </TdNum>
                        <TdNum>{wine.facing}</TdNum>
                        <TdNum>
                          {wine.av_price == null
                            ? '—'
                            : wine.av_price.toFixed(2)}
                        </TdNum>
                      </WineChildRow>
                    )}
                  />
                )}
            </React.Fragment>
          ))
        )}
      </tbody>
    </Table>
  );
};

export const MarketPositioning: React.FC<MarketPositioningProps> = ({
  filter,
  summaryFilter,
  isLoadingPos
}) => {
  const marketColumns = getMarketColumns();
  // Order matches the new spec: Market Ranking, Brand Ranking, Wine Ranking.
  const [selectedSubtab, setSubtab] = useState<
    'positioning' | 'topbrands' | 'topwines'
  >('positioning');
  const subtab = selectedSubtab;
  const [wineLimit, setWineLimit] = useState<RankingLimit>(20);
  const [brandLimit, setBrandLimit] = useState<RankingLimit>(20);
  const [search, setSearch] = useState('');
  const [marketColumnVisibility, setMarketColumnVisibility] = useState(
    defaultMarketColumnVisibility
  );
  const [marketColumnModalOpen, setMarketColumnModalOpen] = useState(false);
  const {
    data: marketSummary,
    isLoading: isLoadingSummary,
    isError: isSummaryError
  } = useV2DistributionMarketSummary(summaryFilter, subtab === 'positioning');

  const { data: wines = [], isLoadingFilters: isLoadingWines } =
    useV2GlobalViewWines(
      {
        ...filter,
        aggregation_type: 'wine',
        result_limit: wineLimit,
        selected_client_ref_ids: null,
        selected_client_custom_ids: null
      },
      subtab === 'topwines'
    );
  const { data: brands = [], isLoadingFilters: isLoadingBrands } =
    useV2GlobalViewWines(
      {
        ...filter,
        aggregation_type: 'brand',
        result_limit: brandLimit,
        selected_client_ref_ids: null,
        selected_client_custom_ids: null
      },
      subtab === 'topbrands'
    );

  const marketRows = useMemo<MarketRow[]>(() => {
    return marketSummary
      .map((summary) => {
        const market = summary.market_name;
        return {
          marketId: summary.market_id,
          rank: 0,
          market,
          categoryND: summary.nd_category,
          categoryPen: summary.category_penetration,
          stores: summary.stores,
          posGained: summary.pos_gained ?? 0,
          posLost: summary.pos_lost ?? 0,
          clientND: summary.nd_client ?? 0,
          clientPen: summary.client_penetration ?? 0,
          rivalND: summary.nd_rival ?? 0,
          rivalPen: summary.rival_penetration ?? 0,
          competitivePoolND: summary.nd_competitive_pool ?? 0,
          competitivePoolPen: summary.competitive_pool_penetration ?? 0,
          ndLastYear: null,
          notSurveyed: null,
          newPosSurveyed: null
        };
      })
      .sort((a, b) => b.stores - a.stores)
      .map((row, index) => ({ ...row, rank: index + 1 }));
  }, [marketSummary]);

  // Brand/Wine Ranking: ranks the whole surveyed catalog by Numerical
  // Distribution (`dn`), independent of ownership, with a real (not
  // simulated) Variation and Rank movement column computed from the
  // already-real `won_pos`/`lost_pos` fields — the "previous" ND is
  // `dn - (won_pos - lost_pos)`, and the previous rank is recomputed by
  // re-sorting on that previous ND (mirrors proto's
  // `gvRankWithEvolution`, minus the 3M/6M/12M period toggle, deferred).
  const toRankingRows = (data: unknown[]): RankingRow[] => {
    const rows = (data as Record<string, unknown>[])
      .map((row) => {
        const accounts = toNumber(row.dn);
        const variation = toNumber(row.won_pos) - toNumber(row.lost_pos);
        return {
          entityId:
            typeof row.entity_id === 'number' ? row.entity_id : undefined,
          name: nameOf(row),
          color: typeof row.color === 'string' ? row.color : null,
          accounts,
          refs: toNumber(row.facing),
          variation,
          prevAccounts: Math.max(0, accounts - variation),
          price: typeof row.av_price === 'number' ? row.av_price : null
        } as RankingRow;
      })
      .filter((row) => row.accounts > 0 || row.refs > 0);

    const byNow = rows
      .slice()
      .sort(
        (a, b) =>
          b.accounts - a.accounts ||
          b.refs - a.refs ||
          a.name.localeCompare(b.name)
      );
    byNow.forEach((row, i) => {
      row.rank = i + 1;
    });
    const byPrev = rows
      .slice()
      .sort(
        (a, b) =>
          b.prevAccounts - a.prevAccounts ||
          b.refs - a.refs ||
          a.name.localeCompare(b.name)
      );
    byPrev.forEach((row, i) => {
      row.prevRank = i + 1;
      row.rankMove = (row.prevRank ?? i + 1) - (row.rank ?? i + 1);
    });
    return byNow;
  };

  const filteredMarketRows = marketRows.filter((row) =>
    row.market.toLowerCase().includes(search.trim().toLowerCase())
  );
  const {
    sortedRows: sortedMarketRows,
    sort: marketSort,
    onSort: sortMarket
  } = useTableSort(
    filteredMarketRows,
    (row, key) => row[key as keyof MarketRow]
  );
  const filteredWines = toRankingRows(wines as unknown[]).filter((row) =>
    row.name.toLowerCase().includes(search.trim().toLowerCase())
  );
  const filteredBrands = toRankingRows(brands as unknown[]).filter((row) =>
    row.name.toLowerCase().includes(search.trim().toLowerCase())
  );
  const isLoading =
    subtab === 'positioning'
      ? isLoadingSummary || isLoadingPos
      : subtab === 'topwines'
        ? isLoadingWines
        : isLoadingBrands;

  const visibleMarketColumns = marketColumns.filter(
    (column) => marketColumnVisibility[column.id]
  );
  const pct = (value: number) => `${value.toFixed(2)}%`;
  const marketCell = (row: MarketRow, id: string): string | number | null => {
    switch (id) {
      case 'market':
        return row.market;
      case 'stores':
        return row.stores;
      case 'categoryND':
        return row.categoryND;
      case 'categoryPen':
        return pct(row.categoryPen);
      case 'clientND':
        return row.clientND;
      case 'clientPen':
        return pct(row.clientPen);
      case 'ndLastYear':
        return row.ndLastYear;
      case 'posGained':
        return row.posGained;
      case 'posLost':
        return -row.posLost;
      case 'notSurveyed':
        return row.notSurveyed;
      case 'newPosSurveyed':
        return row.newPosSurveyed;
      case 'rivalND':
        return row.rivalND;
      case 'rivalPen':
        return pct(row.rivalPen);
      case 'competitivePoolND':
        return row.competitivePoolND;
      default:
        return pct(row.competitivePoolPen);
    }
  };
  const rankingRows = subtab === 'topwines' ? filteredWines : filteredBrands;
  const exportRowCount =
    subtab === 'positioning' ? sortedMarketRows.length : rankingRows.length;
  const exportCurrentView = () => {
    if (subtab === 'positioning') {
      downloadCsv(
        'gdv-market-ranking.csv',
        ['#', ...visibleMarketColumns.map((column) => column.label)],
        sortedMarketRows.map((row) => [
          row.rank,
          ...visibleMarketColumns.map((column) => marketCell(row, column.id))
        ])
      );
      return;
    }
    const isWine = subtab === 'topwines';
    const prefix = isWine
      ? 'v2GlobalView.marketOverview.wineRanking.columns'
      : 'v2GlobalView.marketOverview.brandRanking.columns';
    downloadCsv(
      isWine ? 'gdv-wine-ranking.csv' : 'gdv-brand-ranking.csv',
      [
        '#',
        I18n.t(`${prefix}.${isWine ? 'wine' : 'brand'}`),
        ...(isWine ? [I18n.t(`${prefix}.color`)] : []),
        I18n.t(`${prefix}.accounts`),
        I18n.t(`${prefix}.variation`),
        I18n.t(`${prefix}.refs`),
        I18n.t(`${prefix}.price`)
      ],
      rankingRows.map((row) => [
        row.rank,
        row.name,
        ...(isWine ? [row.color] : []),
        row.accounts,
        row.variation,
        row.refs,
        row.price == null ? null : row.price.toFixed(2)
      ])
    );
  };

  return (
    <Section>
      <Card>
        <CardHeader
          role="group"
          aria-label={I18n.t('v2GlobalView.marketOverview.title')}
        >
          <MarketHeaderMain>
            <Title>
              {I18n.t('v2GlobalView.marketOverview.title')}
              <SectionInfo
                title={I18n.t('v2GlobalView.marketOverview.info.title')}
                body={
                  <>
                    <p>{I18n.t('v2GlobalView.marketOverview.info.intro')}</p>
                    <p>
                      <strong>
                        {I18n.t(
                          'v2GlobalView.marketOverview.subtabs.positioning'
                        )}
                      </strong>{' '}
                      {I18n.t('v2GlobalView.marketOverview.info.marketRanking')}
                    </p>
                    <p>
                      <strong>
                        {I18n.t(
                          'v2GlobalView.marketOverview.subtabs.topbrands'
                        )}
                      </strong>{' '}
                      {I18n.t('v2GlobalView.marketOverview.info.and')}{' '}
                      <strong>
                        {I18n.t('v2GlobalView.marketOverview.subtabs.topwines')}
                      </strong>{' '}
                      {I18n.t(
                        'v2GlobalView.marketOverview.info.catalogRanking'
                      )}
                    </p>
                  </>
                }
              />
            </Title>
            <TabBar
              tabs={[
                {
                  id: 'positioning',
                  label: I18n.t(
                    'v2GlobalView.marketOverview.subtabs.positioning'
                  )
                },
                {
                  id: 'topbrands',
                  label: I18n.t('v2GlobalView.marketOverview.subtabs.topbrands')
                },
                {
                  id: 'topwines',
                  label: I18n.t('v2GlobalView.marketOverview.subtabs.topwines')
                }
              ]}
              activeTab={subtab}
              onTabChange={(id) =>
                setSubtab(id as 'positioning' | 'topbrands' | 'topwines')
              }
              variant="pills"
              ariaLabel={I18n.t('v2GlobalView.marketOverview.title')}
            />
          </MarketHeaderMain>
          <MarketHeaderActions>
            <SearchInput
              className="market-search"
              placeholder={I18n.t(
                'v2GlobalView.marketOverview.searchPlaceholder'
              )}
              value={search}
              onChange={setSearch}
            />
            {subtab === 'positioning' && (
              <Button
                type="button"
                onClick={() => setMarketColumnModalOpen(true)}
              >
                {I18n.t('v2GlobalView.marketOverview.columnDisplay')}
                <MarketColumnBadge>
                  {Object.values(marketColumnVisibility).filter(Boolean).length}
                </MarketColumnBadge>
              </Button>
            )}
            <ExportButton
              onClick={exportCurrentView}
              disabled={isLoading || exportRowCount === 0}
              label={I18n.t('globalView.price.exportCsv')}
            />
          </MarketHeaderActions>
        </CardHeader>
        {isLoading ? (
          <TableSkeleton
            label={I18n.t('v2GlobalView.marketOverview.loading')}
            columns={
              subtab === 'positioning' ? visibleMarketColumns.length + 1 : 6
            }
          />
        ) : subtab === 'positioning' && isSummaryError ? (
          <Loading role="alert">
            {I18n.t('v2GlobalView.marketOverview.error')}
          </Loading>
        ) : subtab === 'positioning' ? (
          <Table>
            <thead>
              <tr>
                <Th
                  aria-sort={
                    marketSort?.key === 'rank' ? marketSort.direction : 'none'
                  }
                >
                  <SortableHeaderButton
                    label={I18n.t('v2GlobalView.marketOverview.ranking')}
                    active={marketSort?.key === 'rank'}
                    direction={marketSort?.direction}
                    onClick={() => sortMarket('rank')}
                  />
                </Th>
                {marketColumns
                  .filter((column) => marketColumnVisibility[column.id])
                  .map((column) => (
                    <Th
                      key={column.id}
                      $align={column.id === 'market' ? 'left' : 'right'}
                      aria-sort={
                        marketSort?.key === column.id
                          ? marketSort.direction
                          : 'none'
                      }
                    >
                      <SortableHeaderButton
                        label={column.label}
                        align={column.id === 'market' ? 'left' : 'right'}
                        active={marketSort?.key === column.id}
                        direction={marketSort?.direction}
                        onClick={() => sortMarket(column.id)}
                      />
                    </Th>
                  ))}
              </tr>
            </thead>
            <tbody>
              {filteredMarketRows.length === 0 ? (
                <tr>
                  <Empty
                    colSpan={
                      1 +
                      marketColumns.filter(
                        (column) => marketColumnVisibility[column.id]
                      ).length
                    }
                  >
                    {I18n.t('v2GlobalView.marketOverview.noMarkets')}
                  </Empty>
                </tr>
              ) : (
                sortedMarketRows.map((row) => (
                  <tr key={row.marketId}>
                    <TdNum>{row.rank}</TdNum>
                    {marketColumns
                      .filter((column) => marketColumnVisibility[column.id])
                      .map((column) => {
                        if (column.id === 'market') {
                          return (
                            <Td key={column.id}>
                              <strong>{row.market}</strong>
                            </Td>
                          );
                        }
                        if (column.id === 'stores') {
                          return <TdNum key={column.id}>{row.stores}</TdNum>;
                        }
                        if (column.id === 'categoryND') {
                          return (
                            <TdNum key={column.id}>{row.categoryND}</TdNum>
                          );
                        }
                        if (column.id === 'categoryPen') {
                          return (
                            <TdNum key={column.id}>
                              {row.categoryPen.toFixed(0)}%
                            </TdNum>
                          );
                        }
                        if (column.id === 'clientND') {
                          return <TdNum key={column.id}>{row.clientND}</TdNum>;
                        }
                        if (column.id === 'clientPen') {
                          return (
                            <TdNum key={column.id}>
                              {row.clientPen.toFixed(0)}%
                            </TdNum>
                          );
                        }
                        if (column.id === 'posGained') {
                          return (
                            <TdNum
                              key={column.id}
                              $positive={row.posGained > 0}
                            >
                              {row.posGained ? `+${row.posGained}` : 0}
                            </TdNum>
                          );
                        }
                        if (column.id === 'posLost') {
                          return (
                            <TdNum key={column.id} $negative={row.posLost > 0}>
                              {row.posLost ? `-${row.posLost}` : 0}
                            </TdNum>
                          );
                        }
                        if (column.id === 'rivalND') {
                          return <TdNum key={column.id}>{row.rivalND}</TdNum>;
                        }
                        if (column.id === 'ndLastYear') {
                          return (
                            <TdNum key={column.id}>
                              {row.ndLastYear ?? '—'}
                            </TdNum>
                          );
                        }
                        if (column.id === 'notSurveyed') {
                          return (
                            <TdNum key={column.id}>
                              {row.notSurveyed ?? '—'}
                            </TdNum>
                          );
                        }
                        if (column.id === 'newPosSurveyed') {
                          return (
                            <TdNum key={column.id}>
                              {row.newPosSurveyed ?? '—'}
                            </TdNum>
                          );
                        }
                        if (column.id === 'competitivePoolND') {
                          return (
                            <TdNum key={column.id}>
                              {row.competitivePoolND}
                            </TdNum>
                          );
                        }
                        if (column.id === 'competitivePoolPen') {
                          return (
                            <TdNum key={column.id}>
                              {row.competitivePoolPen.toFixed(0)}%
                            </TdNum>
                          );
                        }
                        return (
                          <TdNum key={column.id}>
                            {row.rivalPen.toFixed(0)}%
                          </TdNum>
                        );
                      })}
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        ) : (
          <RankingTable
            key={`${subtab}:${JSON.stringify(filter)}`}
            filter={{
              ...filter,
              client_and_rivals_only: false,
              selected_client_ref_ids: null,
              selected_client_custom_ids: null
            }}
            rows={subtab === 'topwines' ? filteredWines : filteredBrands}
            showColor={subtab === 'topwines'}
          />
        )}
        {subtab !== 'positioning' && (
          <RowLimitFooter>
            <RowLimitBar role="group">
              {RANKING_LIMIT_OPTIONS.map((limit) => (
                <GroupByBtn
                  key={limit}
                  type="button"
                  $active={
                    (subtab === 'topwines' ? wineLimit : brandLimit) === limit
                  }
                  aria-pressed={
                    (subtab === 'topwines' ? wineLimit : brandLimit) === limit
                  }
                  disabled={isLoading}
                  onClick={() =>
                    subtab === 'topwines'
                      ? setWineLimit(limit)
                      : setBrandLimit(limit)
                  }
                >
                  {I18n.t('v2GlobalView.marketOverview.showRows', {
                    count: limit
                  })}
                </GroupByBtn>
              ))}
            </RowLimitBar>
          </RowLimitFooter>
        )}
      </Card>
      <ColumnDisplayModal
        open={marketColumnModalOpen}
        onClose={() => setMarketColumnModalOpen(false)}
        columns={marketColumns}
        visible={marketColumnVisibility}
        onApply={setMarketColumnVisibility}
        title={I18n.t('v2GlobalView.modals.marketOverviewColumnDisplayTitle')}
      />
    </Section>
  );
};

const Section = styled.section`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const MarketColumnBadge = styled.span`
  background: var(--terracotta-soft, #fef3ee);
  color: var(--terracotta, #c84b1f);
  border-radius: 999px;
  padding: 1px 7px;
  font-size: 11px;
  font-weight: 700;
`;

const Title = styled.h2`
  font-family: var(--font-sans);
  font-size: 17px;
  font-weight: 600;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  color: var(--ink);
  display: flex;
  align-items: center;
  margin: 0;
  gap: 2px;
`;

const MarketHeaderMain = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
  min-width: 0;
`;

const MarketHeaderActions = styled.div`
  display: flex;
  align-items: center;
  flex: 0 1 460px;
  justify-content: flex-end;
  gap: 10px;
  min-width: 0;
  margin-left: auto;

  > input.market-search {
    flex: 0 1 290px;
    width: 290px;
    max-width: 290px;
    min-width: 180px;
  }
`;

const Card = styled.div`
  overflow: auto;
  border: 1px solid var(--line-soft);
  border-radius: 10px;
  background: #fff;
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  padding: 16px;
  background: #fff;
  border-bottom: 1px solid var(--line-soft);
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  font-family: var(--font-sans, 'DM Sans', sans-serif);
  background: #fff;

  tbody tr:not(:last-child) td {
    border-bottom: 1px solid var(--line-soft, #e8e5df);
  }

  tbody tr:hover {
    background: var(--paper, #f9f7f4);
  }
`;

const Th = styled.th<{ $align?: 'left' | 'right' }>`
  padding: 14px 16px;
  text-align: ${({ $align }) => $align ?? 'left'};
  font-family: var(--font-sans, 'DM Sans', sans-serif);
  font-weight: 600;
  color: var(--ink-faint);
  font-size: 10.5px;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  border-bottom: 1px solid var(--line-soft, #e8e5df);
  white-space: normal;
  overflow-wrap: anywhere;
  line-height: 1.35;
`;

const Td = styled.td`
  padding: 14px 16px;
  vertical-align: middle;
`;

const WineChildRow = styled.tr`
  background: var(--bg);
  td:nth-child(2) {
    padding-left: 48px;
  }
`;

const TdNum = styled(Td)<{ $positive?: boolean; $negative?: boolean }>`
  text-align: right;
  font-variant-numeric: tabular-nums;
  color: ${({ $positive, $negative }) =>
    $positive
      ? 'var(--success, #247a52)'
      : $negative
        ? 'var(--danger, #b43b32)'
        : 'inherit'};
`;

const Empty = styled.td`
  padding: 28px 16px;
  color: var(--ink-faint);
  text-align: center;
`;

const Rank = styled.span`
  display: inline-block;
  width: 26px;
  color: var(--ink-faint);
  font-variant-numeric: tabular-nums;
`;

const RankMove = styled.span<{ $positive?: boolean }>`
  font-size: 11px;
  color: ${({ $positive }) =>
    $positive === undefined
      ? 'var(--ink-faint)'
      : $positive
        ? 'var(--success, #247a52)'
        : 'var(--danger, #b43b32)'};
`;

const Loading = styled.div`
  padding: 48px 16px;
  color: var(--ink-faint);
  text-align: center;
`;
